// Thẻ của hộp hướng dẫn trang Đăng ký ca — MỘT nguồn, buộc vào nhãn thật.
//
// Nhãn lấy thẳng từ `EKIP_SHIFT_TAB_LABEL` của màn, và danh sách khoá khai
// `satisfies` theo đúng khoá của từ điển đó: đổi khoá thẻ trên màn là lỗi kiểu
// ngay lúc build, không phải đợi ai mở hộp lên mới thấy bài chỉ tới một thẻ
// không còn tồn tại.

import type { EKIP_SHIFT_TAB_LABEL } from "@/features/ekip-shift/labels";
import type { Audience } from "./audience";
import type { StatusTone } from "./GuideSections";
import type { GuideFlow, MultiTabGuideConfig } from "./multi-tab-guides";

/** Id trang trong `nav-items.ts`. Tra bằng hằng số, không gõ chuỗi lần hai. */
export const EKIP_SHIFT_GUIDE_ID = "ekip_shifts";

/** Thứ tự thẻ trong hộp = thứ tự thẻ trên màn. */
export const EKIP_SHIFT_GUIDE_TAB_ORDER = [
  "register",
  "approve",
  "absence",
] as const satisfies readonly (keyof typeof EKIP_SHIFT_TAB_LABEL)[];

export type EkipShiftGuideTabKey = (typeof EKIP_SHIFT_GUIDE_TAB_ORDER)[number];

/**
 * Nhóm nghề nào MỞ ĐƯỢC thẻ nào trên màn thật.
 *
 * Nguồn: `features/ekip-shift/permissions.ts` + bảng hành động của máy chủ.
 * Cửa vào trang đã hẹp sẵn (`canReadEkipShifts` chỉ cho khối ảnh, trang điểm,
 * trợ lý studio, kinh doanh, chăm sóc khách hàng cùng vai điều hành), nên bảng
 * này KHÔNG liệt kê các nghề khác — họ không tới được trang.
 *
 *   register  ai đọc được trang
 *   approve   ekipshift.approve  — người chốt lịch của khối
 *   absence   ekipabsence.write hoặc .approve — người nộp đơn, hoặc người xử
 *
 * Lưu ý thẻ Báo nghỉ: trưởng cửa hàng và quản lý cụm KHÔNG nộp được đơn (họ
 * không nằm trong ê-kíp) nhưng xử được đơn, nên vẫn thấy thẻ.
 */
export const THE_AI_MO_DUOC: Record<EkipShiftGuideTabKey, Audience[]> = {
  register: ["photo", "makeup", "assistant", "sale", "cskh", "qc", "lead", "exec"],
  // KHÔNG có "photo"/"makeup"/"assistant"/"sale"/"cskh": thẻ Duyệt trên màn
  // thật gác bằng `ekipshift.approve`, mà quyền đó không cấp cho vai nhân viên
  // thường. Để họ trong bảng này là hộp Hướng dẫn phát trọn bài chốt ca của
  // khối cho người không có thẻ ấy trên màn — đúng loại rò mà tính năng sinh ra
  // để chặn. Riêng khối trợ lý (QĐ 0164) còn chặt hơn: `canApproveDept` trả
  // false cho CẢ vai trưởng phòng mang mã phòng đó, người chốt lịch của họ vào
  // bằng "lead". Trưởng phòng các khối kia đã mang sẵn "lead" nên người duyệt
  // thật không mất bài. Trang Ca lễ tân là khuôn đúng để đối chiếu.
  approve: ["qc", "lead", "exec"],
  absence: ["photo", "makeup", "assistant", "sale", "cskh", "qc", "lead", "exec"],
};

/** Màu nhãn trạng thái, khớp badge trên màn thật. */
const TONE_THEO_NHAN: Record<string, StatusTone> = {
  "Đã duyệt": "success",
  "Đơn nghỉ đã duyệt": "success",
  "Chờ duyệt": "warning",
  "Đơn nghỉ chờ duyệt": "warning",
  "Chưa rõ giờ": "warning",
  "Đã khoá": "warning",
  "Bị từ chối": "danger",
  "Đã huỷ": "danger",
};

/**
 * Vòng đời một tuần làm việc của một khối trên trang này.
 *
 * Trang này có hai mô hình sống song song, CỐ Ý: người khai ca từng tuần, và
 * người mặc định có mặt chỉ khai ngày nghỉ. Dải phải cho thấy cả hai đổ về
 * cùng một chỗ — tuần đã chốt thì máy mới biết ai có mặt để giao suất.
 */
const EKIP_SHIFT_FLOW: GuideFlow = {
  heading: "Một tuần làm việc đi qua đâu",
  intro:
    "Các thẻ của trang là những chặng của cùng một tuần. Chặng tô đậm là chỗ thẻ bạn đang mở nhìn vào.",
  stages: [
    {
      ten: "Khai tuần sau",
      tone: "info",
      ke: "Người khai ca từng tuần thì đăng ký ngày và ca; người mặc định có mặt thì chỉ khai ngày mình nghỉ.",
      the: ["register", "absence"],
    },
    {
      ten: "Chờ duyệt",
      tone: "warning",
      ke: "Ca và đơn nghỉ đều nằm chờ người chốt lịch xem; chờ thì lịch chưa đổi.",
      the: ["approve", "absence"],
    },
    {
      ten: "Đã chốt",
      tone: "success",
      ke: "Ca thành chính thức và ngày nghỉ được ghi nhận — từ đây máy mới biết ai có mặt để giao suất.",
      the: ["register", "absence"],
    },
    {
      ten: "Khoá tuần",
      tone: "danger",
      nhanh: true,
      ke: "Chốt cả tuần của khối: từ lúc đó không ai đăng ký hay sửa ca được nữa.",
      the: ["register", "approve"],
    },
  ],
};

export const EKIP_SHIFT_GUIDE: MultiTabGuideConfig = {
  tabOrder: EKIP_SHIFT_GUIDE_TAB_ORDER,
  theAiMoDuoc: THE_AI_MO_DUOC,
  tone: TONE_THEO_NHAN,
  flow: EKIP_SHIFT_FLOW,
};
